import {
  ALL_SHARD_MODULES,
  CustomModal,
  DEFAULT_FIELDS_BY_COLUMN_NAME,
  FeatureGuide,
  Loader,
  useIsAuthorisedHook
} from '@nile/components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ManagePaymentOptions } from '@nile/client_management/src/Containers/InvoicePaymentMethods';
import CascadeParent from '../../DefaultFields/CascadeParent';
import { Stage, Status } from '../../DefaultFields/ClientDefaultFields';
import Impact from '../../DefaultFields/Impact';
import Priority from '../../DefaultFields/Priority';
import ResolutionCode from '../../DefaultFields/ResolutionCode';
import TicketStatus from '../../DefaultFields/TicketStatus';
import TicketType from '../../DefaultFields/TicketType';
import Urgency from '../../DefaultFields/Urgency';

const ModalWrapper = styled.div`
  width: ${({ sizeAuto }) => (sizeAuto ? 'auto' : '70vw')};
  height: ${({ sizeAuto }) => (sizeAuto ? 'auto' : '90vh')};
  background: #fff;
  border-radius: 10px;
`;

const DefaultFieldModal = (props) => {
  const {
    modalActive,
    setModalActive,
    name,
    loading,
    showWarning,
    updateField,
    module,
    constraints
  } = props;

  const flags = useFlags();
  const defaultFieldsSort = flags?.defaultFieldsSort;

  const [saveLoading, setSaveLoading] = useState(false);

  const isEditAuthorised = useIsAuthorisedHook('customFields.edit');

  const responseCallBack = (data) => {
    if (data?.updateField || data?.updateFieldV2) {
      setModalActive(false);
    }
    setSaveLoading(false);
  };

  const primaryAction = async (variables) => {
    setSaveLoading(true);
    updateField(variables, responseCallBack);
  };
  const moduleName = name;

  const getDefaultContent = () => {
    if (module === ALL_SHARD_MODULES.client) {
      switch (moduleName) {
        case 'stage':
          return (
            <Stage
              primaryAction={primaryAction}
              secondaryAction={() => setModalActive(!modalActive)}
              saveLoading={saveLoading}
              constraints={constraints}
            />
          );
        default:
          return (
            <Status
              primaryAction={primaryAction}
              secondaryAction={() => setModalActive(!modalActive)}
              saveLoading={saveLoading}
              constraints={constraints}
            />
          );
      }
    } else {
      switch (moduleName) {
        case DEFAULT_FIELDS_BY_COLUMN_NAME.IMPACT:
          return (
            <>
              <Impact
                secondaryAction={() => setModalActive(!modalActive)}
                showWarning={showWarning}
                modalActive={modalActive}
                setModalActive={setModalActive}
                primaryAction={primaryAction}
                saveLoading={saveLoading}
                isEditAuthorised={isEditAuthorised}
              />
            </>
          );

        case DEFAULT_FIELDS_BY_COLUMN_NAME.STATUS:
          return (
            <>
              <TicketStatus
                secondaryAction={() => setModalActive(!modalActive)}
                showWarning={showWarning}
                modalActive={modalActive}
                setModalActive={setModalActive}
                primaryAction={primaryAction}
                saveLoading={saveLoading}
                isEditAuthorised={isEditAuthorised}
                disableSort={!defaultFieldsSort}
              />
            </>
          );

        case DEFAULT_FIELDS_BY_COLUMN_NAME.URGENCY:
          return (
            <>
              <Urgency
                secondaryAction={() => setModalActive(!modalActive)}
                showWarning={showWarning}
                modalActive={modalActive}
                setModalActive={setModalActive}
                primaryAction={primaryAction}
                saveLoading={saveLoading}
                isEditAuthorised={isEditAuthorised}
              />
            </>
          );

        case DEFAULT_FIELDS_BY_COLUMN_NAME.PRIORITY:
          return (
            <>
              <Priority
                secondaryAction={() => setModalActive(!modalActive)}
                showWarning={showWarning}
                modalActive={modalActive}
                setModalActive={setModalActive}
                primaryAction={primaryAction}
                saveLoading={saveLoading}
                isEditAuthorised={isEditAuthorised}
              />
            </>
          );

        case DEFAULT_FIELDS_BY_COLUMN_NAME.CAUSE:
          return (
            <>
              <CascadeParent
                secondaryAction={() => setModalActive(!modalActive)}
                showWarning={showWarning}
                modalActive={modalActive}
                setModalActive={setModalActive}
                primaryAction={primaryAction}
                saveLoading={saveLoading}
                columnName={DEFAULT_FIELDS_BY_COLUMN_NAME.CAUSE}
                title={<FormattedMessage id="admin.CauseAndSubcause.header" />}
                subtitle={
                  <FormattedMessage id="admin.CauseAndSubcause.header.label" />
                }
                featureGuide={<FeatureGuide featureName="cause" article />}
                parentTitle={
                  <FormattedMessage id="admin.CauseAndSubcause.cause" />
                }
                childTitle={
                  <FormattedMessage id="admin.CauseAndSubcause.subcause" />
                }
                parentPlaceholderText="admin.CauseAndSubcause.cause.pl"
                childrenPlaceholderText="admin.CauseAndSubcause.subcause.pl.1"
                isEditAuthorised={isEditAuthorised}
                disableSort={!defaultFieldsSort}
              />
            </>
          );

        case DEFAULT_FIELDS_BY_COLUMN_NAME.CATEGORY:
          return (
            <>
              <CascadeParent
                secondaryAction={() => setModalActive(!modalActive)}
                showWarning={showWarning}
                modalActive={modalActive}
                setModalActive={setModalActive}
                primaryAction={primaryAction}
                saveLoading={saveLoading}
                columnName={DEFAULT_FIELDS_BY_COLUMN_NAME.CATEGORY}
                title={
                  <FormattedMessage id="admin.CategorySubCategory.header" />
                }
                subtitle={
                  <FormattedMessage id="admin.CategorySubCategory.header.label" />
                }
                parentTitle={
                  <FormattedMessage id="admin.CategorySubCategory.category" />
                }
                childTitle={
                  <FormattedMessage id="admin.CategorySubCategory.subcategory" />
                }
                parentPlaceholderText="admin.CategorySubCategory.category.pl"
                childrenPlaceholderText="admin.CategorySubCategory.subcategory.pl"
                isEditAuthorised={isEditAuthorised}
                disableSort={!defaultFieldsSort}
              />
            </>
          );

        case DEFAULT_FIELDS_BY_COLUMN_NAME.RESOLUTION_CODE:
          return (
            <>
              <ResolutionCode
                secondaryAction={() => setModalActive(!modalActive)}
                showWarning={showWarning}
                modalActive={modalActive}
                setModalActive={setModalActive}
                primaryAction={primaryAction}
                saveLoading={saveLoading}
                isEditAuthorised={isEditAuthorised}
                disableSort={!defaultFieldsSort}
              />
            </>
          );
        case DEFAULT_FIELDS_BY_COLUMN_NAME.TICKET_TYPE:
          return (
            <>
              <TicketType
                secondaryAction={() => setModalActive(!modalActive)}
                showWarning={showWarning}
                modalActive={modalActive}
                setModalActive={setModalActive}
                saveLoading={saveLoading}
                isEditAuthorised={isEditAuthorised}
                primaryAction={primaryAction}
                disableSort={!defaultFieldsSort}
              />
            </>
          );
        case DEFAULT_FIELDS_BY_COLUMN_NAME.PAYMENT_TERMS:
          return (
            <ManagePaymentOptions
              type="term"
              closeModal={() => setModalActive(!modalActive)}
            />
          );
        case DEFAULT_FIELDS_BY_COLUMN_NAME.PAYMENT_METHOD:
          return (
            <ManagePaymentOptions
              type="method"
              closeModal={() => setModalActive(!modalActive)}
            />
          );
        default:
          return <></>;
      }
    }
  };

  if (loading) {
    return (
      <>
        <CustomModal
          open={modalActive}
          onClose={() => setModalActive(!modalActive)}
        >
          <ModalWrapper className="modal-wrapper">
            <Loader />
          </ModalWrapper>
        </CustomModal>
      </>
    );
  }

  return (
    <>
      <CustomModal
        open={modalActive}
        onClose={() => setModalActive(!modalActive)}
      >
        <ModalWrapper
          sizeAuto={module === ALL_SHARD_MODULES.invoice}
          className="modal-wrapper"
        >
          {getDefaultContent()}
        </ModalWrapper>
      </CustomModal>
    </>
  );
};

export default DefaultFieldModal;
